/**
 * /prijzen — read-only prijslijst voor kantoor.
 *
 * Rendert GET /api/prijzen: de prijsconfiguratie waar de pricing-engine mee
 * rekent (mirror_product2 + mirror_prijsmanagement + staffels + DPA). Geen
 * handmatig bijgehouden lijst dus — wat hier staat is per definitie wat de
 * engine factureert. Bedoeld voor prijsvragen aan de telefoon: "wat kost
 * onbehandeld retour", "wat is de toeslag in gebied X", "welke korting bij
 * meerdere kleden".
 */

import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Spinner } from '../components/ui';
import { supabase } from '../lib/supabase';

interface PrijsStaffel {
  ondergrens: number | null;
  bovengrens: number | null;
  eenheidsprijs: number | null;
  vastePrijs: number | null;
}

interface PrijsRegel {
  policyId: string;
  eenheidsprijs: number | null;
  eenheidsprijsIncl: number | null;
  relatievePrijs: number | null;
  minimalePrijs: number | null;
  priceType: string;
  segment: string | null;
  regiotoeslagToepasbaar: boolean;
  geldigVanaf: string | null;
  geldigTot: string | null;
  status: 'actueel' | 'toekomstig';
  staffels: PrijsStaffel[];
}

interface PrijsProduct {
  productId: string;
  naam: string;
  productCode: string | null;
  familie: string | null;
  segment: string | null;
  btwPercent: number;
  perM2: boolean;
  bundelkorting: boolean;
  prijzen: PrijsRegel[];
}

interface GebiedRij {
  gebied: string | null;
  postcodeBegin: number | null;
  postcodeEinde: number | null;
  segment: string | null;
  regiotoeslag: boolean;
  toeslagPercent: number | null;
  toeslagIngangsdatum: string | null;
  minimumOrderExclBtw: number | null;
}

interface BundelRegel {
  minAantalKleden: number;
  kortingPercent: number;
  minM2PerKleed: number;
  segment: string | null;
}

interface PrijzenResponse {
  peildatum: string;
  producten: PrijsProduct[];
  gebieden: GebiedRij[];
  bundelRegels: BundelRegel[];
  bundelRegelsBeschikbaar: boolean;
}

function euro(v: number | null | undefined): string {
  if (v === null || v === undefined) return '-';
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(v);
}

function incl(excl: number | null, btwPercent: number): number | null {
  if (excl === null) return null;
  return Math.round(excl * (1 + btwPercent / 100) * 100) / 100;
}

function staffelLabel(s: PrijsStaffel, perM2: boolean): string {
  const eenheid = perM2 ? 'm²' : 'st';
  const bereik =
    s.bovengrens != null
      ? `${s.ondergrens ?? 0} - ${s.bovengrens} ${eenheid}`
      : `vanaf ${s.ondergrens ?? 0} ${eenheid}`;
  return bereik;
}

export function PrijzenPage() {
  const [data, setData] = useState<PrijzenResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoek, setZoek] = useState('');
  const [segment, setSegment] = useState<string>('alle');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers: Record<string, string> = {};
        if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
        const response = await fetch('/api/prijzen', { headers });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || `Prijslijst laden mislukt (${response.status})`);
        }
        const payload = (await response.json()) as PrijzenResponse;
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Prijslijst laden mislukt');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const segmenten = useMemo(() => {
    const set = new Set<string>();
    for (const p of data?.producten ?? []) {
      for (const regel of p.prijzen) if (regel.segment) set.add(regel.segment);
    }
    return Array.from(set).sort();
  }, [data]);

  const gefilterd = useMemo(() => {
    const term = zoek.trim().toLowerCase();
    return (data?.producten ?? []).filter((p) => {
      const inSegment =
        segment === 'alle' || p.prijzen.some((regel) => regel.segment === segment);
      if (!inSegment) return false;
      if (!term) return true;
      return (
        p.naam.toLowerCase().includes(term) ||
        (p.productCode ?? '').toLowerCase().includes(term) ||
        (p.familie ?? '').toLowerCase().includes(term)
      );
    });
  }, [data, zoek, segment]);

  const perFamilie = useMemo(() => {
    const map = new Map<string, PrijsProduct[]>();
    for (const p of gefilterd) {
      const key = p.familie ?? 'Overig';
      const list = map.get(key) ?? [];
      list.push(p);
      map.set(key, list);
    }
    return Array.from(map.entries());
  }, [gefilterd]);

  const toeslagGebieden = useMemo(
    () => (data?.gebieden ?? []).filter((g) => g.regiotoeslag && g.toeslagPercent != null),
    [data]
  );
  const minimumStandaard = useMemo(() => {
    // Vrijwel alle gebieden delen hetzelfde order-minimum; toon dat als regel
    // en alleen de afwijkingen apart.
    const counts = new Map<number, number>();
    for (const g of data?.gebieden ?? []) {
      if (g.minimumOrderExclBtw != null) {
        counts.set(g.minimumOrderExclBtw, (counts.get(g.minimumOrderExclBtw) ?? 0) + 1);
      }
    }
    let best: number | null = null;
    let bestCount = 0;
    for (const [v, c] of counts) if (c > bestCount) { best = v; bestCount = c; }
    return best;
  }, [data]);
  const afwijkendeMinimums = useMemo(
    () =>
      (data?.gebieden ?? []).filter(
        (g) => g.minimumOrderExclBtw != null && g.minimumOrderExclBtw !== minimumStandaard
      ),
    [data, minimumStandaard]
  );

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex-1 p-6">
        <p className="text-sm text-red-600">{error ?? 'Prijslijst niet beschikbaar'}</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Prijzen</h1>
            <p className="text-sm text-gray-500">
              Live uit de prijsconfiguratie (peildatum {data.peildatum}). Dit is wat de engine rekent; wijzigen gaat via Prijsmanagement.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="search"
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              placeholder="Zoek product…"
              className="w-64 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <select
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
            >
              <option value="alle">Alle segmenten</option>
              {segmenten.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {perFamilie.map(([familie, producten]) => (
          <Card key={familie}>
            <CardHeader>
              <CardTitle>{familie}</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 pr-4 font-medium">Product</th>
                    <th className="py-2 pr-4 font-medium text-right">Excl. BTW</th>
                    <th className="py-2 pr-4 font-medium text-right">Incl. BTW</th>
                    <th className="py-2 pr-4 font-medium">Eenheid</th>
                    <th className="py-2 pr-4 font-medium text-right">Minimum</th>
                    <th className="py-2 pr-4 font-medium">Bijzonderheden</th>
                  </tr>
                </thead>
                <tbody>
                  {producten.flatMap((p) =>
                    p.prijzen
                      .filter((regel) => segment === 'alle' || regel.segment === segment)
                      .map((regel, i) => {
                        const isStaffel = regel.staffels.length > 0;
                        const bijzonderheden: string[] = [];
                        if (regel.status === 'toekomstig' && regel.geldigVanaf) {
                          bijzonderheden.push(`geldt vanaf ${regel.geldigVanaf}`);
                        }
                        if (regel.relatievePrijs != null) {
                          bijzonderheden.push(`${regel.relatievePrijs}% van de kleedprijs`);
                        }
                        if (regel.regiotoeslagToepasbaar) bijzonderheden.push('regiotoeslag van toepassing');
                        if (p.bundelkorting) bijzonderheden.push('telt mee voor bundelkorting');
                        if (regel.segment && segment === 'alle') bijzonderheden.push(regel.segment);
                        return (
                          <tr
                            key={`${p.productId}-${regel.policyId}`}
                            className={`border-b border-gray-100 ${regel.status === 'toekomstig' ? 'text-gray-400' : ''}`}
                          >
                            <td className="py-2 pr-4">
                              <span className="font-medium text-gray-900">{i === 0 ? p.naam : ''}</span>
                              {i === 0 && p.productCode ? (
                                <span className="ml-2 text-xs text-gray-400">{p.productCode}</span>
                              ) : null}
                            </td>
                            <td className="py-2 pr-4 text-right tabular-nums">
                              {isStaffel ? (
                                <div className="space-y-0.5">
                                  {regel.staffels.map((s, si) => (
                                    <div key={si} className="whitespace-nowrap">
                                      <span className="text-xs text-gray-500">{staffelLabel(s, p.perM2)}: </span>
                                      {s.vastePrijs != null ? euro(s.vastePrijs) : euro(s.eenheidsprijs)}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                euro(regel.eenheidsprijs)
                              )}
                            </td>
                            <td className="py-2 pr-4 text-right tabular-nums">
                              {isStaffel ? (
                                <div className="space-y-0.5">
                                  {regel.staffels.map((s, si) => (
                                    <div key={si} className="whitespace-nowrap">
                                      {s.vastePrijs != null
                                        ? euro(incl(s.vastePrijs, p.btwPercent))
                                        : euro(incl(s.eenheidsprijs, p.btwPercent))}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                euro(regel.eenheidsprijsIncl)
                              )}
                            </td>
                            <td className="py-2 pr-4 text-gray-600">
                              {p.perM2 ? 'per m²' : 'per stuk'}
                              {isStaffel ? ` (${regel.priceType.toLowerCase()})` : ''}
                            </td>
                            <td className="py-2 pr-4 text-right tabular-nums">{euro(regel.minimalePrijs)}</td>
                            <td className="py-2 pr-4 text-xs text-gray-500">{bijzonderheden.join(' · ')}</td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))}

        {perFamilie.length === 0 && (
          <p className="text-sm text-gray-500">Geen producten gevonden voor deze zoekopdracht.</p>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Bundelkorting (meerdere vloerkleden)</CardTitle>
          </CardHeader>
          <CardContent>
            {data.bundelRegelsBeschikbaar ? (
              <ul className="space-y-1 text-sm text-gray-700">
                {data.bundelRegels.map((b, i) => (
                  <li key={i}>
                    Vanaf {b.minAantalKleden} kleden: <span className="font-medium">{b.kortingPercent}% korting</span>
                    {b.minM2PerKleed > 0 ? ` (kleden vanaf ${b.minM2PerKleed} m² tellen mee)` : ''}
                    {b.segment ? ` — ${b.segment}` : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">Bundelkorting-regels zijn nu niet op te halen.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Order-minimum en regiotoeslag</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {minimumStandaard != null && (
              <p className="text-sm text-gray-700">
                Standaard order-minimum: <span className="font-medium">{euro(minimumStandaard)}</span> excl. BTW
                ({euro(Math.round(minimumStandaard * 1.21 * 100) / 100)} incl.) per order.
              </p>
            )}
            {afwijkendeMinimums.length > 0 && (
              <div className="text-sm text-gray-700">
                <p className="font-medium">Afwijkende minimums:</p>
                <ul className="mt-1 space-y-0.5">
                  {afwijkendeMinimums.map((g, i) => (
                    <li key={i}>
                      {g.gebied ?? 'Onbekend gebied'}
                      {g.postcodeBegin != null ? ` (${g.postcodeBegin}-${g.postcodeEinde})` : ''}: {euro(g.minimumOrderExclBtw)} excl.
                      {g.segment ? ` — ${g.segment}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {toeslagGebieden.length > 0 ? (
              <div className="text-sm text-gray-700">
                <p className="font-medium">Regiotoeslag-gebieden (op producten waar de toeslag op van toepassing is):</p>
                <div className="mt-1 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                        <th className="py-1.5 pr-4 font-medium">Gebied</th>
                        <th className="py-1.5 pr-4 font-medium">Postcodes</th>
                        <th className="py-1.5 pr-4 font-medium text-right">Toeslag</th>
                        <th className="py-1.5 pr-4 font-medium">Segment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {toeslagGebieden.map((g, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-1.5 pr-4">{g.gebied ?? '-'}</td>
                          <td className="py-1.5 pr-4 tabular-nums">
                            {g.postcodeBegin != null ? `${g.postcodeBegin} - ${g.postcodeEinde}` : '-'}
                          </td>
                          <td className="py-1.5 pr-4 text-right tabular-nums">{g.toeslagPercent}%</td>
                          <td className="py-1.5 pr-4">{g.segment ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Geen gebieden met regiotoeslag.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
